// The part of a house agent that talks to the hub and decides what the lights
// show. Uses only standard APIs (WebSocket, timers) so the browser simulator
// runs exactly this code for its simulated houses.
//
// Shows are absolute-time state, not one-off events: if the hub connection
// drops, the agent keeps rendering the last show from its own clock (a beat
// show keeps beating), then catches up when it reconnects.
//
// While a song plays, the hub sends a song cue (which song, when it started,
// and this house's program). The agent downloads the song's analysis once and
// renders the program on every strand from what the music is doing at that
// moment; outside the song it falls back to the show.

import type { AgentConfig, AgentReport, ClientMessage, Show, SongCue, StrandConfig } from "../shared/protocol.ts";
import { parseServerMessage } from "../shared/validate.ts";
import { renderShow, sameFrame, type Frame, type RenderStyle } from "../shared/effects.ts";
import { parseAnalysis } from "../shared/library.ts";
import { momentAt, songClock, type SongClock } from "../shared/moment.ts";
import { renderProgram } from "../shared/program.ts";
import type { Output, OutputDriver } from "./driver.ts";

export const AGENT_VERSION = "0.1.0";

// Puts a house's strands in order and works out how each one renders: by the
// hub's per-strand order, else discovery order. Flash style is for strands
// that fade (standard mode); real-time strands snap and don't need it.
export function layoutStrands(
  outputs: Output[],
  strands: StrandConfig[],
  flash: boolean,
): { id: string; style: RenderStyle }[] {
  const config = new Map(strands.map((s) => [s.id, s]));
  const placed = outputs.map((o, found) => ({ o, found, order: config.get(o.id)?.order ?? found }));
  placed.sort((a, b) => a.order - b.order || a.found - b.found);
  return placed.map(({ o }, i) => ({
    id: o.id,
    style: {
      flash: flash && !o.realtime,
      sections: o.sections,
      strand: i,
      strands: placed.length,
      reverse: config.get(o.id)?.reverse === true,
    },
  }));
}

export interface AgentOptions {
  hubUrl: string;
  // Street Pis sign in with their house's device key; the hub then supplies
  // the house, position and settings. Development rooms use room + key + house.
  token?: string;
  room: string;
  key: string;
  house: string;
  position: number;
  driver: OutputDriver;
  // House-wide flash style, for strands in standard mode.
  style?: Pick<RenderStyle, "flash">;
  tickMs?: number;
  statusIntervalMs?: number;
  pingIntervalMs?: number;
  reconnectMinMs?: number;
  reconnectMaxMs?: number;
  log?: (line: string) => void;
  onConnectionChange?: (connected: boolean) => void;
  // Lets the simulator cut a house's connection; defaults to the global one.
  WebSocketImpl?: typeof WebSocket;
  // Downloads a song's analysis (JSON). Default: from the hub with the device key.
  loadAnalysis?: (songId: string) => Promise<unknown>;
  // A song whose download failed (e.g. the site was restarting) is tried
  // again this often while it's still the one playing (default 5 s).
  songRetryMs?: number;
}

// Songs whose analysis stays in memory (a show cycles through a playlist).
const SONG_CACHE = 4;

const CLOCK_SKEW_WARN_MS = 100;

export class AgentClient {
  readonly opts: Required<Omit<AgentOptions, "onConnectionChange" | "token" | "loadAnalysis">> &
    Pick<AgentOptions, "onConnectionChange" | "token" | "loadAnalysis">;
  offsetMs = 0;
  clockSkewMs: number | null = null;
  connected = false;
  current: Show | null = null;
  private pending: Show | null = null;
  // Per-strand settings from the hub, and the last frame sent to each strand.
  strands: StrandConfig[] = [];
  private lastFrames = new Map<string, Frame>();
  // The song playing (or being tried) and its decoded analysis.
  cue: SongCue | null = null;
  private songs = new Map<string, SongClock>();
  private loading = new Set<string>();
  private songRetryAt = 0;
  private ws: WebSocket | null = null;
  private running = false;
  private backoffMs: number;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: AgentOptions) {
    this.opts = {
      style: {},
      tickMs: 20,
      statusIntervalMs: 5000,
      pingIntervalMs: 60000,
      reconnectMinMs: 1000,
      reconnectMaxMs: 30000,
      songRetryMs: 5000,
      log: (line) => console.log(line),
      WebSocketImpl: globalThis.WebSocket,
      ...options,
    };
    this.backoffMs = this.opts.reconnectMinMs;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // A street Pi touches no lights until the hub sends the household's list
    // (consent), even for the moment before its settings arrive.
    if (this.opts.token) this.opts.driver.configure?.({ allowedDevices: [] });
    this.timers.push(setInterval(() => this.tick(), this.opts.tickMs));
    this.timers.push(setInterval(() => this.sendStatus(), this.opts.statusIntervalMs));
    this.timers.push(setInterval(() => this.sendPing(), this.opts.pingIntervalMs));
    this.connect();
  }

  stop(): void {
    this.running = false;
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.ws?.close();
    this.ws = null;
    this.setConnected(false);
  }

  // The shared timeline as seen by this house: shifted by its offset.
  timelineNow(): number {
    return Date.now() + this.offsetMs;
  }

  private connect(): void {
    if (!this.running) return;
    let ws: WebSocket;
    try {
      ws = new this.opts.WebSocketImpl(this.opts.hubUrl);
    } catch (e) {
      this.opts.log(`Bad hub URL ${this.opts.hubUrl}: ${(e as Error).message}`);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      if (this.opts.token) {
        this.send({ type: "hello", role: "agent", token: this.opts.token, agentVersion: AGENT_VERSION });
        return;
      }
      this.send({
        type: "hello",
        role: "agent",
        room: this.opts.room,
        key: this.opts.key,
        house: this.opts.house,
        position: this.opts.position,
        agentVersion: AGENT_VERSION,
      });
    });
    ws.addEventListener("message", (event) => {
      if (typeof event.data === "string") this.handleMessage(event.data);
    });
    ws.addEventListener("close", (event) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setConnected(false);
      if (!this.running) return;
      const reason = event.reason ? ` (${event.reason})` : "";
      this.opts.log(`Hub connection closed: ${event.code}${reason}. Keeping the last show running.`);
      this.scheduleReconnect();
    });
    // Errors are always followed by close, which handles reconnecting.
    ws.addEventListener("error", () => {});
  }

  private scheduleReconnect(): void {
    if (!this.running || this.reconnectTimer) return;
    const delay = this.backoffMs * (0.75 + Math.random() * 0.5);
    this.backoffMs = Math.min(this.backoffMs * 2, this.opts.reconnectMaxMs);
    this.opts.log(`Reconnecting in ${(delay / 1000).toFixed(1)}s`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private handleMessage(text: string): void {
    let msg;
    try {
      msg = parseServerMessage(text);
    } catch (e) {
      this.opts.log(`Ignoring bad message from hub: ${(e as Error).message}`);
      return;
    }
    switch (msg.type) {
      case "welcome":
        this.backoffMs = this.opts.reconnectMinMs;
        this.setConnected(true);
        this.offsetMs = msg.offsetMs ?? 0;
        this.opts.log(
          this.opts.token ? "Connected to the street hub" : `Connected to hub as ${this.opts.house} in room ${this.opts.room}`,
        );
        if (msg.show) this.receiveShow(msg.show);
        this.sendPing();
        this.sendStatus();
        break;
      case "show":
        this.receiveShow(msg.show);
        break;
      case "config":
        this.applyConfig(msg);
        break;
      case "song":
        this.receiveCue(msg.cue);
        break;
      case "pong": {
        const now = Date.now();
        const rtt = now - msg.clientTime;
        this.clockSkewMs = Math.round(msg.clientTime + rtt / 2 - msg.serverTime);
        if (Math.abs(this.clockSkewMs) > CLOCK_SKEW_WARN_MS) {
          this.opts.log(
            `Warning: this clock differs from the hub by about ${this.clockSkewMs} ms ` +
              `(round trip ${rtt} ms). Check that NTP time sync is on.`,
          );
        }
        this.sendStatus();
        break;
      }
      case "error":
        this.opts.log(`Hub error: ${msg.message}`);
        break;
      case "houses":
        break;
    }
  }

  // Exposed for tests.
  applyConfig(config: AgentConfig): void {
    if (config.offsetMs !== this.offsetMs) this.opts.log(`Offset set to ${config.offsetMs} ms`);
    this.offsetMs = config.offsetMs;
    if (config.position !== undefined) this.opts.position = config.position;
    if (config.style) this.opts.style = { flash: config.style.flash };
    if (config.strands) this.strands = config.strands;
    this.opts.driver.configure?.({
      realtime: config.style?.realtime,
      allowedDevices: config.allowedDevices,
      strands: config.strands,
    });
    this.lastFrames.clear(); // re-send the current frames under the new settings
  }

  // Exposed for tests.
  receiveCue(cue: SongCue | null): void {
    this.cue = cue;
    this.lastFrames.clear();
    if (cue) this.loadSong(cue.songId);
  }

  private loadSong(songId: string): void {
    if (this.songs.has(songId) || this.loading.has(songId)) return;
    const load = this.opts.loadAnalysis ?? ((id: string) => this.fetchAnalysis(id));
    this.loading.add(songId);
    load(songId)
      .then((raw) => {
        const analysis = parseAnalysis(raw);
        if (!analysis) throw new Error("the analysis isn't valid");
        this.songs.set(songId, songClock(analysis));
        // Keep only the most recent few.
        while (this.songs.size > SONG_CACHE) this.songs.delete(this.songs.keys().next().value!);
      })
      .catch((e: Error) => {
        this.songRetryAt = Date.now() + this.opts.songRetryMs;
        this.opts.log(`Couldn't load song ${songId}: ${e.message}. Trying again in ${this.opts.songRetryMs / 1000} s.`);
      })
      .finally(() => this.loading.delete(songId));
  }

  private async fetchAnalysis(songId: string): Promise<unknown> {
    if (!this.opts.token) throw new Error("no device key");
    const base = this.opts.hubUrl.replace(/^ws(s?):\/\//, "http$1://").replace(/\/+$/, "");
    const res = await fetch(`${base}/api/agent/songs/${songId}/analysis`, {
      headers: { authorization: `Bearer ${this.opts.token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  private receiveShow(show: Show): void {
    // Re-delivery of the show we already have (e.g. after reconnecting).
    if (this.current && this.current.t === show.t && this.current.kind === show.kind) return;
    if (show.t <= this.timelineNow()) {
      this.current = show;
      this.pending = null;
    } else {
      // Latest wins: a newer scheduled show replaces an older one not yet started.
      this.pending = show;
    }
  }

  // Exposed for tests and the simulator.
  tick(): void {
    const at = this.timelineNow();
    if (this.cue && !this.songs.has(this.cue.songId) && Date.now() >= this.songRetryAt) this.loadSong(this.cue.songId);
    if (this.pending && at >= this.pending.t) {
      this.current = this.pending;
      this.pending = null;
    }
    const layout = layoutStrands(this.opts.driver.outputs(), this.strands, this.opts.style.flash === true);
    const songFrames = this.songFrames(at, layout);
    if (!songFrames && !this.current) return; // never told anything: leave the lights alone
    // One frame per strand; only the ones that changed go to the driver.
    const changed = new Map<string, Frame>();
    for (const { id, style } of layout) {
      const frame = songFrames?.get(id) ?? renderShow(this.current!, at, this.opts.position, style);
      if (sameFrame(frame, this.lastFrames.get(id) ?? null)) continue;
      this.lastFrames.set(id, frame);
      changed.set(id, frame);
    }
    if (changed.size) this.opts.driver.apply(changed);
  }

  // The program's frames while a cued song is playing and its analysis is
  // ready; null otherwise (then the show plays).
  private songFrames(at: number, layout: { id: string; style: RenderStyle }[]): Map<string, Frame> | null {
    const cue = this.cue;
    const clock = cue ? this.songs.get(cue.songId) : undefined;
    if (!cue || !clock) return null;
    const t = (at - cue.startEpochMs) / 1000;
    if (t < 0 || t > cue.duration) return null;
    return renderProgram(cue.program, momentAt(clock, t), at, this.opts.position, layout);
  }

  private sendStatus(): void {
    if (!this.connected) return;
    const report: AgentReport = {
      driver: this.opts.driver.name,
      devices: this.opts.driver.devices(),
      clockSkewMs: this.clockSkewMs,
    };
    this.send({ type: "status", report });
  }

  private sendPing(): void {
    if (this.connected) this.send({ type: "ping", clientTime: Date.now() });
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private setConnected(connected: boolean): void {
    if (this.connected === connected) return;
    this.connected = connected;
    this.opts.onConnectionChange?.(connected);
  }
}
